import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SettaSwitch from '../../components/settaSwitch';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/protocolClose.css';

const descricoesPorCategoria = {
    Alteração: ['Titularidade', 'Demanda', 'Rateio', 'Enquadramento'],
    Solicitação: ['Segunda Via', 'Demonstrativo'],
    Correção: ['Faturamento', 'Injetado'],
    'Rede Celpe': [
        'Falta de Energia', 'Qualidade', 'Religação', 'Falta de fase',
        'Desligamento Definitivo', 'Elo Fusível', 'Chave Faca', 'Sobretensão', 'Não Especificado'
    ]
};

const FinalizarProtocolo = () => {
    const [cc, setCC] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [statusProtocolo, setStatusProtocolo] = useState('');
    const [categoria, setCategoria] = useState('');
    const [descricao, setDescricao] = useState('');
    const [listDescricao, setListDescricao] = useState([]);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [protocolo, setProtocolo] = useState('');
    const [tipo, setTipo] = useState('cc');
    const [isSubmit, setIsSubmit] = useState(false);

    const isByProtocol = tipo === 'protocolo';

    const selectCategoria = (e) => {
        const catAtual = e.target.value;
        setCategoria(catAtual);
        setListDescricao(descricoesPorCategoria[catAtual]);
        setDescricao('');
    };

    const handleTipoChange = (checked) => {
        setTipo(checked ? 'protocolo' : 'cc');
        setError(false);
        setCC('');
        setProtocolo('');
        setDateTime(null);
        setCategoria('');
        setDescricao('');
        setListDescricao([]);
    };

    async function submitFinalizarProtocolos (e) {
        e.preventDefault();
        setIsSubmit(true);

        let data = '';
        if (dateTime) {
            setError(false);
            data = formatIsoDate(dateTime);
        } else if (!dateTime && tipo === 'cc') {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            cc,
            dataFinal: data,
            statusProtocolo,
            descricao,
            protocolo
        });

        try {
            const response = await fetchFlask('fetchFinalizarProtocolo', body);
            setMessage(response.message);
            setStatusAlerta(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setCC('');
                setDateTime(null);
                setCategoria('');
                setDescricao('');
                setListDescricao([]);
                setProtocolo('');
                setStatusProtocolo('');
            }
        } finally {
            setIsSubmit(false);
        }
    }

    const dataLabel = formatDateLabelPtBr(dateTime);
    const modeLabel = isByProtocol ? 'Por protocolo' : 'Por conta contrato';
    const completedFields = isByProtocol
        ? [protocolo, statusProtocolo].filter(Boolean).length
        : [cc, dateTime, categoria, descricao, statusProtocolo].filter(Boolean).length;
    const totalFields = isByProtocol ? 2 : 5;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={statusAlerta}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Finalizar Protocolo
            </PageHeader>

            <form action='' method='POST' id='formFinalizarProtocolo' onSubmit={submitFinalizarProtocolos} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Encerramento de protocolo</h2>
                        </Box>
                        <p>
                            Finalize o atendimento por protocolo direto ou por conta contrato com categoria, descrição e data de conclusão.
                        </p>
                    </Box>

                    <Box className='protocol-close-mode-panel'>
                        <Box className='protocol-close-mode-copy'>
                            {isByProtocol ? <DescriptionRoundedIcon /> : <TaskAltRoundedIcon />}
                            <Box>
                                <span>Modo de encerramento</span>
                                <strong>{modeLabel}</strong>
                            </Box>
                        </Box>

                        <Box className='protocol-close-mode-switch'>
                            <span>CC</span>
                            <SettaSwitch
                                checked={isByProtocol}
                                onChange={handleTipoChange}
                                ariaLabel='Alternar entre finalizar por conta contrato e por protocolo'
                            />
                            <span>Protocolo</span>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <DescriptionRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>{isByProtocol ? 'Protocolo' : 'Conta contrato'}</strong>
                            </Box>
                        </Box>

                        {isByProtocol
                            ? (
                                <TextField
                                    required
                                    label='Protocolo'
                                    value={protocolo}
                                    onChange={(e) => setProtocolo(e.target.value)}
                                />
                            )
                            : (
                                <Box className='ui-fields-grid'>
                                    <TextField
                                        required
                                        label='Conta Contrato'
                                        value={cc}
                                        onChange={(e) => setCC(e.target.value)}
                                    />

                                    <FormControl className='ui-control' error={error}>
                                        <DayDatePicker
                                            id='data'
                                            label='Data de Finalização'
                                            minDate={new Date('2023-01-02')}
                                            maxDate={new Date()}
                                            value={dateTime}
                                            onChange={setDateTime}
                                        />
                                        {error && (
                                            <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                        )}
                                    </FormControl>
                                </Box>
                            )}
                    </Box>

                    {!isByProtocol && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <FactCheckRoundedIcon />
                                <Box>
                                    <span>Etapa 2</span>
                                    <strong>Classificação</strong>
                                </Box>
                            </Box>

                            <Box className='ui-fields-grid'>
                                <FormControl required className='ui-control'>
                                    <InputLabel id='labelCat'>Categoria</InputLabel>
                                    <Select
                                        id='categoria'
                                        labelId='labelCat'
                                        label='Categoria'
                                        value={categoria}
                                        onChange={selectCategoria}
                                    >
                                        <MenuItem value='Alteração'>Alteração</MenuItem>
                                        <MenuItem value='Solicitação'>Solicitação</MenuItem>
                                        <MenuItem value='Correção'>Correção</MenuItem>
                                        <MenuItem value='Rede Celpe'>Rede Celpe</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl required className='ui-control'>
                                    <InputLabel id='labelDescricao'>Descrição</InputLabel>
                                    <Select
                                        id='descricao'
                                        labelId='labelDescricao'
                                        label='Descrição'
                                        value={descricao}
                                        onChange={(e) => setDescricao(e.target.value)}
                                    >
                                        {listDescricao.map((item) => (
                                            <MenuItem key={item} value={item}>{item}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    )}

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <CheckCircleRoundedIcon />
                            <Box>
                                <span>{isByProtocol ? 'Etapa 2' : 'Etapa 3'}</span>
                                <strong>Status de conclusão</strong>
                            </Box>
                        </Box>

                        <FormControl required className='ui-control'>
                            <InputLabel id='labelStatus'>Status de Conclusão</InputLabel>
                            <Select
                                id='status'
                                labelId='labelStatus'
                                label='Status de Conclusão'
                                value={statusProtocolo}
                                onChange={(e) => setStatusProtocolo(e.target.value)}
                            >
                                <MenuItem value='CONCLUÍDO'>Concluído</MenuItem>
                                <MenuItem value='ENCERRADO'>Encerrado</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do encerramento</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Modo</span>
                            <strong>{modeLabel}</strong>
                        </Box>
                        <Box>
                            <span>Conta contrato</span>
                            <strong className={!cc ? 'is-empty' : undefined}>{cc || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Protocolo</span>
                            <strong className={!protocolo ? 'is-empty' : undefined}>{protocolo || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Data final</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{isByProtocol ? 'Não exigida' : dataLabel}</strong>
                        </Box>
                        <Box>
                            <span>Categoria</span>
                            <strong className={!categoria ? 'is-empty' : undefined}>{isByProtocol ? 'Não exigida' : categoria || 'Não selecionada'}</strong>
                        </Box>
                        <Box>
                            <span>Descrição</span>
                            <strong className={!descricao ? 'is-empty' : undefined}>{isByProtocol ? 'Não exigida' : descricao || 'Não selecionada'}</strong>
                        </Box>
                        <Box>
                            <span>Status</span>
                            <strong className={!statusProtocolo ? 'is-empty' : undefined}>{statusProtocolo || 'Não selecionado'}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <EventAvailableRoundedIcon />
                        <p>
                            Confira o modo de encerramento antes de enviar. Por CC, a data e a classificação são obrigatórias.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/{totalFields} preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Finalizar Protocolo
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default FinalizarProtocolo;
